/**
 * CardHeader component with avatar, user info, thought, and timestamp.
 * Based on Figma design.
 */

import React, { FC, useRef, useState } from 'react';
import { styled } from 'linaria/react';

import useTheme from 'helpers/useTheme';
import { FONTS } from '../../../styles/constants';
import { ReactComponent as AddReaction } from '../../../assets/icons/AddReaction.svg';
import ReactionMenu from '../../Reactions/ReactionMenu/ReactionMenu';
import { CardHeaderProps } from '../types';
import Avatar from './Avatar';
import { isColorDark, getTextSecondary } from './colorUtils';

const AVATAR_SIZE = 44;

const StyledHeader = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 0 12px 20px 12px;
  width: 100%;
`;

const StyledAvatarContainer = styled.div`
  flex-shrink: 0;
  width: ${AVATAR_SIZE}px;
  height: ${AVATAR_SIZE}px;
`;

const StyledWrapper = styled.div`
  flex: 1 0 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 1px;
  min-width: 1px;
  padding-top: 2px;
`;

const StyledUserInfo = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 14px;
  line-height: 1.4;
  width: 100%;
`;

const StyledNameRow = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
`;

const StyledName = styled.span`
  font-family: ${FONTS.secondary};
  font-weight: 600;
  color: var(--card-text-primary, #181800);
  flex-shrink: 0;
`;

const StyledFollowing = styled.span`
  font-family: ${FONTS.secondary};
  font-weight: 400;
  color: var(--card-text-secondary, #7a7359);
  flex: 1 0 0;
  min-height: 1px;
  min-width: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledBio = styled.p`
  font-family: ${FONTS.secondary};
  font-weight: 400;
  color: var(--card-text-primary, #181800);
  white-space: pre-wrap;
  width: 100%;
  margin: 0;
`;

const StyledThoughtSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const StyledThoughtLine = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  padding-right: 44px;
  position: relative;
  width: 100%;
`;

const StyledThought = styled.div`
  flex: 1 0 0;
  min-width: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  font-family: ${FONTS.secondary};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--card-text-primary, #181800);
`;

const StyledTimestamp = styled.div`
  display: flex;
  gap: 2px;
  align-items: flex-start;
  font-size: 12px;
  line-height: 1.4;
  color: var(--card-text-secondary, #7a7359);
  width: 100%;
`;

const StyledTimestampText = styled.span`
  font-family: ${FONTS.secondary};
  font-weight: 400;
  white-space: nowrap;
`;

const StyledListTitle = styled.span`
  font-family: ${FONTS.secondary};
  font-weight: 600;
  white-space: pre-wrap;
  flex: 1 0 0;
  min-height: 1px;
  min-width: 1px;
`;

const StyledContentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`;

const StyledContentLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const StyledReactionButton = styled.button`
  flex-shrink: 0;
  height: 24px;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0 0 0 8px;

  &:hover {
    opacity: 0.7;
  }
`;

const CardHeader: FC<CardHeaderProps> = ({
  avatars,
  name,
  bio,
  isFollowing,
  thought,
  timestamp,
  listTitle,
  backgroundColor,
  onOpenReactionsModal,
  className,
  onThoughtClick,
  fadeThought,
}) => {
  const primaryAvatar = avatars[0];
  const displayName = name ?? primaryAvatar?.name ?? primaryAvatar?.username;

  const isDarkTheme = useTheme();
  // Contrast icon color against card background, falling back to theme
  const darkBg = backgroundColor ? isColorDark(backgroundColor) : isDarkTheme;
  const iconColor = getTextSecondary(darkBg);

  // Reaction menu state
  const addReactionButtonRef = useRef<HTMLButtonElement>(null);
  const [isReactionMenuOpen, setIsReactionMenuOpen] = useState(false);
  const [reactionMenuCoords, setReactionMenuCoords] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const openReactionMenu = () => {
    if (addReactionButtonRef.current) {
      const rect = addReactionButtonRef.current.getBoundingClientRect();
      setReactionMenuCoords({
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
      });
    }
    setIsReactionMenuOpen(true);
  };

  const closeReactionMenu = () => setIsReactionMenuOpen(false);

  const quotedThought = thought ? `"${thought}"` : null;

  return (
    <StyledHeader className={className}>
      {primaryAvatar && (
        <StyledAvatarContainer>
          <Avatar
            src={primaryAvatar.avatarUrl}
            fallbackLetter={primaryAvatar.username?.charAt(0) ?? displayName?.charAt(0) ?? '?'}
            size={AVATAR_SIZE}
          />
        </StyledAvatarContainer>
      )}

      <StyledWrapper>
        {/* User info section */}
        <StyledUserInfo>
          <StyledNameRow>
            {displayName && <StyledName>{displayName}</StyledName>}
            {isFollowing && <StyledFollowing>{` · Following`}</StyledFollowing>}
          </StyledNameRow>
          {bio && <StyledBio>{bio}</StyledBio>}
        </StyledUserInfo>

        {/* Thought/fallback section with reaction icon */}
        {(thought || listTitle || timestamp) && (
          <StyledContentRow>
            <StyledContentLeft
              onClick={
                onThoughtClick
                  ? (e: React.MouseEvent) => {
                      e.stopPropagation();
                      onThoughtClick();
                    }
                  : undefined
              }
              style={onThoughtClick ? { cursor: 'pointer' } : undefined}
            >
              <StyledThoughtSection>
                {quotedThought ? (
                  <StyledThoughtLine>
                    <StyledThought
                      style={
                        fadeThought
                          ? {
                              background: 'linear-gradient(90deg, currentColor 73%, rgba(255, 255, 255, 0.2) 96%)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text',
                            }
                          : undefined
                      }
                    >
                      {quotedThought}
                    </StyledThought>
                  </StyledThoughtLine>
                ) : (
                  listTitle && (
                    <StyledTimestamp>
                      <StyledTimestampText>Added to</StyledTimestampText>
                      <StyledListTitle>{listTitle}</StyledListTitle>
                    </StyledTimestamp>
                  )
                )}

                {/* Timestamp - only show separately if there's a thought */}
                {thought && (timestamp || listTitle) && (
                  <StyledTimestamp>
                    {timestamp && <StyledTimestampText>{timestamp}</StyledTimestampText>}
                    {listTitle && (
                      <>
                        <StyledTimestampText>· in</StyledTimestampText>
                        <StyledListTitle>{listTitle}</StyledListTitle>
                      </>
                    )}
                  </StyledTimestamp>
                )}

                {/* Fallback: show timestamp alone when no thought or listTitle */}
                {!thought && !listTitle && timestamp && (
                  <StyledTimestamp>
                    <StyledTimestampText>{timestamp}</StyledTimestampText>
                  </StyledTimestamp>
                )}
              </StyledThoughtSection>
            </StyledContentLeft>

            {/* Reaction icon aligned right */}
            <div
              style={{ position: 'relative', zIndex: 100, pointerEvents: 'auto' }}
              onClick={(e) => e.stopPropagation()}
            >
              <StyledReactionButton
                ref={addReactionButtonRef}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  openReactionMenu();
                }}
                type="button"
                aria-label="Add reaction"
                style={{ color: iconColor }}
              >
                <AddReaction />
              </StyledReactionButton>
              <ReactionMenu
                isOpen={isReactionMenuOpen}
                anchorPoint={reactionMenuCoords}
                onClose={closeReactionMenu}
                onSend={() => {}}
                onEmoji={() => {}}
                onOpenReactionsModal={onOpenReactionsModal}
              />
            </div>
          </StyledContentRow>
        )}
      </StyledWrapper>
    </StyledHeader>
  );
};

export default CardHeader;
